import { type ReactNode } from 'react';

import { Box as Flex, Typography as Text, styled } from '@material-ui/core';

import { RiCloseLine } from '@remixicon/react';

import {
  ColorCategory,
  SEMANTIC_COLOR_OPTIONS,
  SemanticColor,
  useColorResolver,
} from '../../utils';

export type ColorPickerVariant = 'filled' | 'outlined';

export type ColorPickerProps = {
  label: string;
  category: ColorCategory;
  currentValue: SemanticColor | null | undefined;
  onSelect: (semanticColor: SemanticColor | null) => void;
  variant?: ColorPickerVariant;
  icon?: ReactNode;
};

const ColorGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(5, 1fr)',
  gap: theme.spacing(1.5),
}));

const baseSwatchStyles = {
  width: 24,
  height: 24,
  borderRadius: 'var(--encore-corner-radius-base)',
  cursor: 'pointer',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 'var(--encore-text-size-smaller-2)',
  fontWeight: 600,
  transition: 'transform 0.1s ease',
};

const getFilledSwatchBorder = (color: string | null, isActive: boolean) => {
  if (isActive) return '1px solid var(--text-base)';
  if (color === null) return '1px solid var(--decorative-subdued)';
  return 'none';
};

const filledSwatchStyles = (color: string | null, isActive: boolean) => ({
  ...baseSwatchStyles,
  border: getFilledSwatchBorder(color, isActive),
  backgroundColor: color ?? 'transparent',
  color: 'var(--text-base)',
});

const outlinedSwatchStyles = (color: string | null, isActive: boolean) => ({
  ...baseSwatchStyles,
  border: isActive
    ? `2px solid ${color ?? 'var(--text-base)'}`
    : `1px solid ${color ?? 'var(--decorative-subdued)'}`,
  backgroundColor: 'transparent',
  color: color ?? 'var(--text-base)',
});

const getSwatchStylesFn = (variant: ColorPickerVariant) => {
  return variant === 'filled' ? filledSwatchStyles : outlinedSwatchStyles;
};

const ColorSwatchBase = styled('button')({
  '&:hover': {
    transform: 'scale(1.1)',
  },
  '&:focus': {
    outline: 'none',
    borderColor: 'var(--essential-base)',
  },
});

const SectionLabel = styled(Text)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 500,
  color: 'var(--text-subdued)',
  marginBottom: theme.spacing(1.5),
  letterSpacing: '0.5px',
}));

type ColorItem = {
  name: string;
  semanticName: SemanticColor | null;
};

const ALL_COLOR_OPTIONS: ColorItem[] = [
  { name: 'None', semanticName: null },
  ...SEMANTIC_COLOR_OPTIONS,
];

export const ColorPicker = ({
  label,
  category,
  currentValue,
  onSelect,
  variant = 'filled',
  icon,
}: ColorPickerProps) => {
  const { resolveTextColor, resolveBackgroundColor } = useColorResolver();

  const resolveColor =
    category === 'text' ? resolveTextColor : resolveBackgroundColor;

  const getSwatchStyles = getSwatchStylesFn(variant);

  const isActive = (semanticName: SemanticColor | null): boolean => {
    if (semanticName === null) {
      return !currentValue;
    }
    return currentValue === semanticName;
  };

  return (
    <Flex
      display="flex"
      flexDirection="column"
      style={{
        gap: 'var(--encore-spacing-tighter-4)',
      }}
    >
      <SectionLabel>{label}</SectionLabel>
      <ColorGrid>
        {ALL_COLOR_OPTIONS.map(({ name, semanticName }) => {
          const displayColor = resolveColor(semanticName);
          const isNoneOption = semanticName === null;

          return (
            <ColorSwatchBase
              key={name}
              style={getSwatchStyles(displayColor, isActive(semanticName))}
              onClick={() => onSelect(semanticName)}
              title={name}
              type="button"
             data-track-source-anchor="87b3388577;color-picker-color-swatch-base">
              {isNoneOption ? (
                <RiCloseLine size={24} color="var(--essential-subdued)" />
              ) : (
                icon
              )}
            </ColorSwatchBase>
          );
        })}
      </ColorGrid>
    </Flex>
  );
};
